import React, { useEffect, useState } from "react";
import { FcGoogle } from "react-icons/fc";
import { FaMicrosoft } from "react-icons/fa";
import { ETitle, AuthProviderEnum, ETagLines } from "@/types/types";
import { Button } from "@/components";
import { AppVersion } from "kmstack-shared/components";

interface IAuthCommon {
    children: React.ReactNode;
    title: ETitle;
    tagLine?: React.ReactNode;
    callback: (provider: AuthProviderEnum) => void;
    hideOtherLogins?: boolean;
}

export const AuthLayout: React.FC<IAuthCommon> = ({
    children,
    title,
    tagLine,
    callback,
    hideOtherLogins = false,
}) => {
    const [titleSet, setTitleSet] = useState<ETitle>(title);
    const [taglineSet, setTaglineSet] = useState<React.ReactNode>("");

    useEffect(() => {
        switch (title) {
            case ETitle.RESET:
                setTitleSet(ETitle.RESET);
                setTaglineSet(ETagLines.RESET);
                break;
            case ETitle.SIGNUP:
                setTitleSet(ETitle.SIGNUP);
                setTaglineSet(ETagLines.SIGNUP);
                break;
            case ETitle.TFA:
                setTitleSet(ETitle.TFA);
                setTaglineSet(ETagLines.TFA);
                break;
            case ETitle.LOGIN:
            default:
                setTitleSet(ETitle.LOGIN);
                setTaglineSet(ETagLines.LOGIN);
                break;
        }
    }, [title]);

    const authContext = titleSet === ETitle.LOGIN
        ? {
            eyebrow: "Secure finance command center",
            headline: "Welcome back",
            description: "Approve payroll, reconcile payments, and manage client finance workflows with enterprise-grade access control.",
            proof: [
                ["Payroll approvals", "Controlled before release"],
                ["Bank feeds", "Reconciled by workspace"],
                ["Audit trail", "Every action traceable"],
            ],
        }
        : titleSet === ETitle.SIGNUP
            ? {
                eyebrow: "Launch your workspace",
                headline: "Build your finance stack",
                description: "Bring invoicing, payroll, compliance, and banking operations into one secure operating layer.",
                proof: [
                    ["Multi-org setup", "Separate teams and roles"],
                    ["Payroll controls", "Approvals ready from day one"],
                    ["Compliance records", "Built for audit context"],
                ],
            }
            : {
                eyebrow: "Account recovery",
                headline: "Security first",
                description: "Recover access through protected verification designed for finance teams and multi-organization workspaces.",
                proof: [
                    ["Identity checks", "Protected recovery flow"],
                    ["Session control", "Secure workspace access"],
                    ["Audit trail", "Recovery events recorded"],
                ],
            };

    return (
        <div className="auth-forms min-h-screen bg-[#f6f7f4] text-slate-950 lg:grid lg:grid-cols-[minmax(0,1.03fr)_minmax(520px,0.97fr)]">
            {/* LEFT PANEL IMAGE */}
            <div className="relative hidden overflow-hidden lg:block">
                <img
                    className="absolute inset-0 h-full w-full object-cover"
                    src="https://images.unsplash.com/photo-1554774853-719586f82d77?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1170&q=80"
                    alt="Secure and efficient workspace"
                />
                <div className="absolute inset-0 bg-[linear-gradient(120deg,rgba(8,20,13,0.88),rgba(8,20,13,0.56)_42%,rgba(8,20,13,0.2))]"></div>
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_18%_16%,rgba(132,204,22,0.28),transparent_26%),radial-gradient(circle_at_90%_86%,rgba(255,255,255,0.18),transparent_24%)]"></div>

                <div className="relative z-10 flex h-full flex-col justify-between p-10 xl:p-14">
                    <div className="inline-flex w-fit items-center gap-3 rounded-lg border border-white/15 bg-white/10 px-4 py-2 text-xs font-black uppercase text-white/85 backdrop-blur-md">
                        <span className="h-2 w-2 rounded-full bg-primaryGreen shadow-[0_0_18px_rgba(103,151,0,0.95)]"></span>
                        Kmstack account portal
                    </div>

                    <div className="max-w-xl pb-4">
                        <p className="mb-4 text-xs font-black uppercase text-primaryGreen">
                            {authContext.eyebrow}
                        </p>
                        <h1 className="text-5xl font-black leading-[0.95] text-white xl:text-6xl">
                            {authContext.headline}
                        </h1>
                        <p className="mt-5 max-w-lg text-base font-medium leading-7 text-white/75">
                            {authContext.description}
                        </p>

                        <div className="mt-8 grid max-w-2xl grid-cols-3 gap-3">
                            {authContext.proof.map(([label, value]) => (
                                <div key={label} className="rounded-lg border border-white/10 bg-white/10 p-4 backdrop-blur-md">
                                    <p className="text-[10px] font-black uppercase text-white/50">{label}</p>
                                    <p className="mt-2 text-sm font-black text-white">{value}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* RIGHT PANEL AUTH CARD */}
            <div className="relative flex w-full flex-col items-center justify-center overflow-hidden px-4 py-10 sm:px-6 lg:px-12">
                <div className="absolute inset-0 bg-[linear-gradient(135deg,#fbfcf8_0%,#f7f9f1_46%,#ffffff_100%)]"></div>
                <div className="relative w-full max-w-[700px]">
                    <div className="rounded-lg border border-slate-100 bg-white p-9 sm:p-12">
                        <div className="mb-8 flex items-center justify-between border-b border-slate-100 pb-5">
                            <div className="flex items-center gap-3">
                                <img
                                    src="/assets/logo-green.svg"
                                    className="h-9 w-9"
                                />
                                <div>
                                    <p className="text-sm font-black text-slate-950">Kmstack</p>
                                    <p className="text-xs font-semibold text-slate-400">Account portal</p>
                                </div>
                            </div>
                            <div className="rounded-lg bg-[#f2f6e9] px-3 py-2 text-[10px] font-black uppercase text-[#3f6f00]">
                                Secure access
                            </div>
                        </div>
                        {/* LOGO */}
                        <div className="mb-8 text-left">
                            <h2 className="max-w-xl text-3xl font-black leading-tight text-slate-950">
                                {titleSet}
                            </h2>
                            <p className="mt-3 max-w-md text-sm font-semibold leading-6 text-slate-500">{tagLine ?? taglineSet}</p>
                        </div>

                        {/* THIRD PARTY AUTH */}
                        {!hideOtherLogins && (
                            <div className="mb-7 grid grid-cols-2 gap-3">
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => callback(AuthProviderEnum.GOOGLE)}
                                    className="h-12 rounded-lg border-slate-200 bg-white shadow-sm transition-all hover:border-slate-900 hover:bg-slate-950 hover:text-white"
                                >
                                    <FcGoogle className="h-5 w-5 mr-2" />
                                    <span className="text-xs font-semibold uppercase">Google</span>
                                </Button>

                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => callback(AuthProviderEnum.MICROSOFT)}
                                    className="h-12 rounded-lg border-slate-200 bg-white shadow-sm transition-all hover:border-slate-900 hover:bg-slate-950 hover:text-white"
                                >
                                    <FaMicrosoft className="h-5 w-5 mr-2 text-blue-600" />
                                    <span className="text-xs font-semibold uppercase">Microsoft</span>
                                </Button>
                            </div>
                        )}

                        {/* "OR" DIVIDER */}
                        {!hideOtherLogins && (
                            <div className="relative my-7">
                                <div className="absolute inset-0 flex items-center">
                                    <div className="w-full border-t border-slate-100" />
                                </div>
                                <div className="relative flex justify-center text-xs uppercase font-bold">
                                    <span className="bg-white px-4 text-slate-400">or</span>
                                </div>
                            </div>
                        )}

                        {/* FORM CONTENT */}
                        {children}

                        {/* FOOTER */}
                        <div className="mt-8 grid grid-cols-3 gap-2 border-t border-slate-100 pt-5 text-left">
                            {["Encrypted", "RBAC", "Audit-ready"].map((item) => (
                                <span key={item} className="rounded-lg bg-slate-50 px-3 py-2 text-center text-[10px] font-black uppercase text-slate-500">
                                    {item}
                                </span>
                            ))}
                        </div>
                        <p className="mt-5 text-center text-xs font-semibold text-slate-400">
                            <AppVersion />
                            © {new Date().getFullYear()} Kmstack. All rights reserved.
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};
